import React, { createContext, useContext, useState, useCallback, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

export const translations = {
  de: {
    nav: { approach: "Ansatz", founder: "Gründer", assessment: "CRA-Check", services: "Leistungen", insights: "Insights", contact: "Kontakt", cta: "Erstgespräch" },
    hero: {
      overline: "EU Digital Regulation · Cyber Resilience Act",
      l1: "Compliance,",
      l2: "die aus dem",
      l3: "Unternehmen denkt.",
      sub: "NexTech.Law verbindet 20 Jahre unternehmerische Praxis mit tiefer regulatorischer Expertise im EU Cyber Resilience Act — ein ganzheitlicher Ansatz statt isolierter Rechtsberatung. NexTech.Law denkt Compliance aus der Perspektive von Unternehmen: regulatorisch fundiert, unternehmerisch und auf die praktische Umsetzung ausgerichtet.",
      ctaPrimary: "Fällt mein Produkt unter den CRA?",
      ctaSecondary: "Erstgespräch buchen",
    },
    marquee: "EU CYBER RESILIENCE ACT · DORA · NIS2 · GDPR · FRACTIONAL CCO · TECH LAW · PRODUKT-COMPLIANCE · ",
    founder: {
      overline: "Der Gründer",
      fullName: "Mag. Michael Jesse, LL.M., CEMS MIM",
      title: "Jurist, Stratege, Unternehmer – in einer Person.",
      body: "Michael ist Jurist mit Schwerpunkt auf europäischem Technologie- und Digitalrecht und verbindet diese Expertise mit strategischer Unternehmensberatung sowie über 20 Jahren internationaler Unternehmer- und Managementerfahrung. Er kennt Regulierung aus drei Perspektiven: rechtlich, strategisch und operativ.",
      highlight: "Diese Kombination kann kein klassischer Anwalt und kein reiner Berater replizieren.",
      linkedin: "Auf LinkedIn vernetzen",
      stats: [
        { v: "20+ Jahre", l: "Internationale Business-, Management- und Technologieerfahrung" },
        { v: "Keynote · Trainer · Advisor", l: "Regulierung und Technologie verständlich für Management und Teams" },
        { v: "35+ Länder", l: "Internationale Teams, Kunden und Stakeholder über Kulturen hinweg geführt" },
        { v: "3 Masterabschlüsse", l: "Tech Law · Wirtschaftswissenschaften · Internationales Management" },
      ],
    },
    manifesto: {
      overline: "CRA Compliance-as-a-Service",
      title: "Von der Einordnung bis zur laufenden Umsetzung.",
      steps: [
        {
          n: "01",
          t: "Diagnose",
          d: "Wir klären Scope, Produktklasse, Wirtschaftsakteursrolle und Konformitätsweg und entwickeln einen konkreten CRA-Fahrplan für Ihr Unternehmen.",
          outcome: "Ergebnis: klare Einordnung, Prioritäten und ein umsetzbarer CRA-Fahrplan.",
        },
        {
          n: "02",
          t: "Framework",
          d: "Wir schaffen die erforderlichen Strukturen – von Risikobewertung und SBOM über Supportzeitraum und Schwachstellenmanagement bis zur technischen Dokumentation.",
          outcome: "Fortschritt: ein Framework, das zu Ihren Produkten, Prozessen und Ihrer Organisation passt.",
        },
        {
          n: "03",
          t: "Umsetzung",
          d: "Ich begleite Ihre Teams bei der praktischen Implementierung und integriere die CRA-Anforderungen in Produkte, Prozesse und Verantwortlichkeiten.",
          outcome: "Ziel: CRA-Compliance, die im Unternehmen tatsächlich funktioniert.",
        },
        {
          n: "04",
          t: "Laufender Betrieb",
          d: "Planbare CRA-Begleitung zum fixen monatlichen Preis – inklusive regelmäßiger Compliance-Reviews, Management-Sparring, fachlicher Beratung, Prüfung aktueller Fragestellungen und Unterstützung bei der laufenden Umsetzung.",
          outcome: "Vom ersten CRA-Check bis zum laufenden Compliance-Betrieb – ein Ansprechpartner über den gesamten Lebenszyklus.",
        },
      ],
      collageTitle: "Produkte, die unter den CRA fallen:",
      collage: [
        { k: "software", label: "Software" },
        { k: "iot", label: "IoT & Smart Home" },
        { k: "hardware", label: "Hardware / Chips" },
        { k: "network", label: "Networking" },
      ],
    },
    assessment: {
      overline: "CRA-Applicability-Check",
      title: "Fällt Ihr Produkt unter den Cyber Resilience Act?",
      sub: "Beantworten Sie einige Fragen zu Ihrem Produkt, dessen Vermarktung und Datenverbindungen und erhalten Sie eine strukturierte CRA-Ersteinschätzung.",
    },
    craCheck: {
      overline: "CRA-Applicability-Check",
      title: "Fällt Ihr Produkt unter den Cyber Resilience Act?",
      sub: "Beantworten Sie einige Fragen zu Ihrem Produkt, dessen Vermarktung und Datenverbindungen und erhalten Sie eine strukturierte CRA-Ersteinschätzung.",
      methodOverline: "Drei-Schritte-Scope-Test · CRA v1.8",
      methodDesc: "Der Check basiert auf der offiziellen CRA-Quellenbasis (Verordnung (EU) 2024/2847, Kommissionsleitlinie C(2026) 5252, CRA FAQs v1.3, Blue Guide) und der Drei-Schritte-Scope-Methode von Michael Jesse.",
      start: "CRA-Check starten",
      disclaimer: "Automatisierte vorläufige Ersteinschätzung — keine abschließende Compliance-Bewertung, keine Rechtsberatung.",
    },
    contact: {
      overline: "Kontakt",
      title: "Sprechen wir über Ihre CRA-Strategie.",
      sub: "Unverbindliches Erstgespräch. Wir melden uns innerhalb von 24 Stunden.",
      name: "Name", email: "E-Mail", company: "Unternehmen", message: "Ihre Nachricht",
      send: "Nachricht senden", sending: "Wird gesendet…",
      success: "Nachricht wurde erfolgreich versendet. Wir melden uns innerhalb 24h bei Ihnen.",
      error: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
    },
    footer: { tagline: "EU Tech Compliance · Cyber Resilience Act Advisory", rights: "Alle Rechte vorbehalten.", imprint: "Impressum", privacy: "Datenschutz", terms: "AGB", linkedin: "LinkedIn", newsletter: "Newsletter", cookieSettings: "Cookie-Einstellungen ändern" },
    consent: {
      processing1: "Ich stimme zu, dass ",
      processingAria: "Einwilligung: Datenverarbeitung",
      marketingAria: "Einwilligung: Marketing-Kommunikation",
      brand: "NexTech.Law",
      processing2: " meine personenbezogenen Daten zum Zweck der Bearbeitung meiner Anfrage und zur Kontaktaufnahme verarbeitet. Weitere Informationen finde ich in der ",
      processingLink: "Datenschutzerklärung",
      processing3: ".",
      marketing1: "Ich stimme zu, von ",
      marketing2: " weitere Informationen, Einladungen und fachliche Updates per E-Mail zu erhalten. Diese Einwilligung kann ich jederzeit mit Wirkung für die Zukunft widerrufen.",
      required: "Bitte stimmen Sie der Datenverarbeitung zu, um Ihre Anfrage abzusenden.",
    },
    newsletter: {
      overline: "EU-Tech Law Newsletter",
      title: "Insights zu EU-Regulierung, CRA und Tech Law — direkt aus der Praxis.",
      sub: "Regelmäßige Updates auf LinkedIn: neue Vorgaben, praktische Auslegung und was Unternehmen konkret tun müssen.",
      cta: "Auf LinkedIn abonnieren",
      badge: "Kostenlos · Auf LinkedIn",
    },
  },
  en: {
    nav: { approach: "Approach", founder: "Founder", assessment: "CRA Check", services: "Services", insights: "Insights", contact: "Contact", cta: "Book a call" },
    hero: {
      overline: "EU Digital Regulation · Cyber Resilience Act",
      l1: "Compliance",
      l2: "that thinks like",
      l3: "a business.",
      sub: "NexTech.Law combines 20 years of entrepreneurial practice with deep regulatory expertise in the EU Cyber Resilience Act — a holistic approach instead of isolated legal advice. NexTech.Law approaches compliance from the perspective of businesses: grounded in regulation, entrepreneurial, and focused on practical implementation.",
      ctaPrimary: "Does your product fall under the CRA?",
      ctaSecondary: "Book a call",
    },
    marquee: "EU CYBER RESILIENCE ACT · DORA · NIS2 · GDPR · FRACTIONAL CCO · TECH LAW · PRODUCT COMPLIANCE · ",
    founder: {
      overline: "The Founder",
      fullName: "Michael Jesse, MSc, LL.M, CEMS MIM",
      title: "Legal counsel, strategist, entrepreneur – in one person.",
      body: "Michael is a legal counsel focused on European technology and digital law and combines that expertise with strategic management consulting and over 20 years of international entrepreneurial and management experience. He knows regulation from three perspectives: legal, strategic and operational.",
      highlight: "A combination no classic attorney and no pure consultant can replicate.",
      linkedin: "Connect on LinkedIn",
      stats: [
        { v: "20+ years", l: "International business, management and technology experience" },
        { v: "Keynote · Trainer · Advisor", l: "Making regulation and technology tangible for management and teams" },
        { v: "35+ countries", l: "Led international teams, clients and stakeholders across cultures" },
        { v: "3 Master's Degrees", l: "Tech Law · Business & Economics · International Management" },
      ],
    },
    manifesto: {
      overline: "CRA Compliance-as-a-Service",
      title: "From assessment to ongoing execution.",
      steps: [
        {
          n: "01",
          t: "Diagnose",
          d: "We clarify scope, product class, economic-operator role and conformity assessment path and develop a concrete CRA roadmap for your company.",
          outcome: "Outcome: clear scoping, priorities and an actionable CRA roadmap.",
        },
        {
          n: "02",
          t: "Framework",
          d: "We build the required structures — from risk assessment and SBOM to support period and vulnerability management to technical documentation.",
          outcome: "Progress: a framework that fits your products, processes and organisation.",
        },
        {
          n: "03",
          t: "Execution",
          d: "I support your teams during practical implementation and embed CRA requirements into products, processes and responsibilities.",
          outcome: "Goal: CRA compliance that actually works inside the company.",
        },
        {
          n: "04",
          t: "Ongoing operations",
          d: "Predictable CRA support at a fixed monthly rate — including regular compliance reviews, management sparring, expert advice, review of current questions and support with ongoing execution.",
          outcome: "From the first CRA check to ongoing compliance operations — one partner across the entire lifecycle.",
        },
      ],
      collageTitle: "Products that fall under the CRA:",
      collage: [
        { k: "software", label: "Software" },
        { k: "iot", label: "IoT & Smart Home" },
        { k: "hardware", label: "Hardware / Chips" },
        { k: "network", label: "Networking" },
      ],
    },
    assessment: {
      overline: "CRA Applicability Check",
      title: "Does your product fall under the Cyber Resilience Act?",
      sub: "Answer a few questions about your product, how it is made available on the market and its data connections to receive a structured preliminary CRA assessment.",
    },
    craCheck: {
      overline: "CRA Applicability Check",
      title: "Does your product fall under the Cyber Resilience Act?",
      sub: "Answer a few questions about your product, how it is made available on the market and its data connections to receive a structured preliminary CRA assessment.",
      methodOverline: "Three-step scope test · CRA v1.8",
      methodDesc: "The check is based on the official CRA source base (Regulation (EU) 2024/2847, Commission Guidance C(2026) 5252, CRA FAQs v1.3, Blue Guide) and the three-step scope methodology by Michael Jesse.",
      start: "Start CRA Check",
      disclaimer: "Automated preliminary assessment — not a final compliance determination, not legal advice.",
    },
    contact: {
      overline: "Contact",
      title: "Let's talk about your CRA strategy.",
      sub: "Non-binding intro call. We respond within 24 hours.",
      name: "Name", email: "Email", company: "Company", message: "Your message",
      send: "Send message", sending: "Sending…",
      success: "Message sent successfully. We will get back to you within 24 hours.",
      error: "Something went wrong. Please try again.",
    },
    footer: { tagline: "EU Tech Compliance · Cyber Resilience Act Advisory", rights: "All rights reserved.", imprint: "Imprint", privacy: "Privacy", terms: "Terms", linkedin: "LinkedIn", newsletter: "Newsletter", cookieSettings: "Change cookie settings" },
    consent: {
      processing1: "I agree that ",
      processingAria: "Consent: data processing",
      marketingAria: "Consent: marketing communication",
      brand: "NexTech.Law",
      processing2: " may process my personal data for the purpose of handling my inquiry and contacting me. Further information can be found in the ",
      processingLink: "Privacy Policy",
      processing3: ".",
      marketing1: "I agree to receive further information, invitations and professional updates from ",
      marketing2: " by email. I may withdraw this consent at any time with effect for the future.",
      required: "Please consent to data processing before submitting your request.",
    },
    newsletter: {
      overline: "EU Tech Law Newsletter",
      title: "Insights on EU regulation, CRA and tech law — straight from practice.",
      sub: "Regular updates on LinkedIn: new requirements, practical interpretation and what companies actually need to do.",
      cta: "Subscribe on LinkedIn",
      badge: "Free · On LinkedIn",
    },
  },
};

const I18nContext = createContext(null);

const LANG_STORAGE_KEY = "nl_lang";

// Routes with a fixed, URL-determined language. The URL always wins so that
// /services is German and /en/services is English at first render and during
// hydration — localStorage or browser language never override it.
export const routeLang = (pathname) => {
  if (pathname === "/en/services") return "en";
  if (pathname === "/services") return "de";
  return null;
};

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window !== "undefined") {
      const forced = routeLang(window.location.pathname);
      if (forced) return forced;
    }
    return "de";
  });
  const t = translations[lang];
  const setLang = useCallback((l) => {
    setLangState(l);
    try { localStorage.setItem(LANG_STORAGE_KEY, l); } catch (_) {}
  }, []);

  // Apply a stored language choice only AFTER the first render (hydration-safe)
  // and never on routes whose language is fixed by the URL.
  useEffect(() => {
    if (routeLang(window.location.pathname)) return;
    try {
      const stored = localStorage.getItem(LANG_STORAGE_KEY);
      if (stored && translations[stored]) setLangState((cur) => (cur === stored ? cur : stored));
    } catch (_) {}
  }, []);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

// Forces the context language to match the URL on language-fixed routes.
// useLayoutEffect: runs synchronously before paint, so no wrong-language frame.
export const LangRouteSync = () => {
  const { pathname } = useLocation();
  const { lang, setLang } = useI18n();
  useLayoutEffect(() => {
    const forced = routeLang(pathname);
    if (forced && forced !== lang) setLang(forced);
  }, [pathname, lang, setLang]);
  return null;
};

export const useI18n = () => useContext(I18nContext);
